<template>
  <div>

    <a v-if="canCreate(permissionKey)" href="#/scenarios/0/new" :disabled="!isActive" class="button is-success">
      <span class="icon">
        <i class="fa fa-plus-circle"></i>
      </span>
      <span>{{ $t('scenarios.createScenario') }}</span>
    </a>
    <div class="control is-grouped">
      <button class="button is-info is-outlined" :disabled="true">
        <span class="icon">
          <i class="fa fa-search"></i>
        </span>
        <span>{{ $t('common.search') }}</span>
      </button>
      <input v-model="searchName" class="input" type="text" v-bind:placeholder="$t('scenarios.putScenarioName')" v-on:change="searchByName">
    </div>
    <div class="tile is-ancestor">
      <div class="tile is-parent">
        <article class="tile is-child box">
          <div class="table-responsive">
            <table class="table is-bordered is-striped is-narrow">
              <thead>
                <tr>
                  <th @click="sort('name')">{{$t('common.title')}}{{sortIcon('name')}}</th>
                  <th @click="sort('client')">{{$t('common.client')}}{{sortIcon('client')}}</th>
                  <th @click="sort('template')">{{$t('common.template')}}{{sortIcon('template')}}</th>
                  <th @click="sort('isReady')">{{$t('common.readiness')}}{{sortIcon('isReady')}}</th>
                  <th @click="sort('dateCreated')">{{$t('common.creationDate')}}{{sortIcon('dateCreated')}}</th>
                  <th>{{$t('common.action')}}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(scenario, index) in sortedScenarios">
                  <td>
                    <a :href="'#/scenarios/0/'+scenario.id">{{scenario.name}}</a>
                  </td>
                  <td>
                    {{scenario.client}}
                  </td>
                  <td>
                    {{scenario.template}}
                  </td>
                  <td>
                    <button v-if="scenario.isReady" class="button is-success is-outlined" :disabled="true" style="width:100%">
                      <span class="icon">
                        <i class="fa fa-info"></i>
                      </span>
                      <span>{{$t('scenarios.prepared')}}</span>
                    </button>
                    <button v-else class="button is-danger is-outlined" :disabled="true" style="width:100%">
                      <span class="icon">
                        <i class="fa fa-info"></i>
                      </span>
                      <span>{{$t('scenarios.notPrepared')}}</span>
                    </button>
                  </td>
                  <td>
                    {{getDate(scenario.dateCreated)}}
                  </td>
                  <td>
                    <button v-if="isRemove(index) && canDelete(permissionKey)" :disabled="!isActive" class="button is-light" v-on:click="openModal(scenario, 'delete')" style="width:100%">
                      <span class="icon">
                        <i class="fa fa-minus-circle"></i>
                      </span>
                      <span>{{$t('common.actions.delete')}}</span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div id="page-navigation">
            <ul class="pagination">
              <li class="pagination-item">
                <button
                  type="button"
                  @click="onClickFirstPage"
                  :disabled="isInFirstPage"
                  aria-label="Go to first page"
                >
                  {{$t('paging.first')}}
                </button>
              </li>
              <li class="pagination-item">
                <button
                  type="button"
                  @click="onClickPreviousPage"
                  :disabled="isInFirstPage"
                  aria-label="Go to previous page"
                >
                  {{$t('paging.previous')}}
                </button>
              </li>
              <li v-for="page in pages" class="pagination-item">
                <button
                  type="button"
                  @click="onClickPage(page.name)"
                  :disabled="page.isDisabled"
                  :class="{ activepage: isPageActive(page.name) }"
                  :aria-label="`Go to page number ${page.name}`"
                >
                  {{ page.name }}
                </button>
              </li>
              <li class="pagination-item">
                <button
                  type="button"
                  @click="onClickNextPage"
                  :disabled="isInLastPage"
                  aria-label="Go to next page"
                >
                  {{$t('paging.next')}}
                </button>
              </li>
              <li class="pagination-item">
                <button
                  type="button"
                  @click="onClickLastPage"
                  :disabled="isInLastPage"
                  aria-label="Go to last page"
                >
                  {{$t('paging.last')}}
                </button>
              </li>
            </ul>
          </div>
        </article>
        <modal :visible="showModal" :title=title @confirm="confirmAction" @close="closeModal"></modal>
      </div>
    </div>
  </div>
</template>

<script>

  import { mapMutations, mapGetters } from 'vuex'
  import Modal from './Modal'
  import Vue from 'vue'
  import Notification from 'vue-bulma-notification'
  import Message from 'vue-bulma-message'
  import CheckToken from '../authentication/CheckToken'
  import CheckPermission from '../authentication/CheckPermission'

  Vue.mixin(CheckToken)
  Vue.mixin(CheckPermission)

  const NotificationComponent = Vue.extend(Notification)
  const openNotification = (propsData = {
    title: '',
    message: '',
    type: '',
    direction: '',
    duration: 4500,
    container: '.notifications'
  }) => {
    return new NotificationComponent({
      el: document.createElement('div'),
      propsData
    })
  }

  const MessageComponent = Vue.extend(Message)
  const openMessage = (propsData = {
    title: '',
    message: '',
    type: '',
    direction: '',
    duration: 10000,
    container: '.messages'
  }) => {
    return new MessageComponent({
      el: document.createElement('div'),
      propsData
    })
  }

  const api = '/api/scenarios'
  const PROFILES_API = '/api/profiles'

export default {
  components: {
    Modal,
    Message,
    Notification
  },

  data () {
    return {
      scenarios: [],
      sortedScenarios: [],
      currentPage: 1,
      perPage: 10,
      maxVisibleButtons: 10,
      currentSort: 'dateCreated',
      currentSortDir: 'desc',
      ascIcon: ' ▲',
      descIcon: ' ▼',
      searchName: '',
      scenarioToDelete: null,
      showModal: null,
      title: '',
      isActive: false,
      permissionKey: 'scenario'
    }
  },

  created: function () {
    this.checkToken()
    this.loadState()
    this.loadData()
  },

  computed: {
    ...mapGetters([
      'loggedId',
      'authenticationToken'
    ]),
    totalPages() {
      return Math.max(Math.ceil(this.scenarios.length / this.perPage), 1)
    },
    startPage() {
      if (this.currentPage === 1) {
        return 1
      }

      if (this.currentPage === this.totalPages) {
        return Math.max(this.totalPages - this.maxVisibleButtons + 1, 1)
      }

      return this.currentPage - 1
    },
    endPage() {
      return Math.min(this.startPage + this.maxVisibleButtons - 1, this.totalPages)
    },
    pages() {
      const range = []
      for (let i = this.startPage; i <= this.endPage; i += 1) {
        range.push({
          name: i,
          isDisabled: i === this.currentPage
        })
      }
      return range
    },
    isInFirstPage() {
      return this.currentPage === 1
    },
    isInLastPage() {
      return this.currentPage === this.totalPages
    }
  },

  methods: {
    loadData () {
      this.setLoadedScenario(null)
      let that = this
      this.$http.get(api, {headers: this.prepareSecurityHeader()})
        .then((response) => {
          this.scenarios = response.data
          this.sortedScenarios = this.sortedScenario()
      })
        .catch((error) => {
        console.log(error)
          this.openNotificationWithType('danger', that.$i18n.messages[that.$i18n.locale].notifications.scenario.errorLoadScenarioList)
      })
    },
    ...mapMutations([
      'setLoadedScenario'
    ]),
    sortedScenario() {
      return this.scenarios
        .filter(this.filterByName)
        .sort((a, b) => {
        let modifier = 1
        if (this.currentSortDir === 'desc') modifier = -1
        if (a[this.currentSort] < b[this.currentSort]) return -1 * modifier
        if (a[this.currentSort] > b[this.currentSort]) return 1 * modifier
        return 0
      }).filter((row, index) => {
        let start = (this.currentPage - 1) * this.perPage
        let end = this.currentPage * this.perPage
        if (index >= start && index < end) return true
      })
    },
    sort(s) {
      // if s == current sort, reverse
      if (s === this.currentSort) {
        this.currentSortDir = this.currentSortDir === 'asc' ? 'desc' : 'asc'
      }
      this.currentSort = s
      this.sortedScenarios = this.sortedScenario()
    },
    filterByName : function(scenario) {
      if (this.searchName.length === 0) {
        return true
      }
      return (scenario.name.toLowerCase().indexOf(this.searchName.toLowerCase()) > -1)
    },
    searchByName() {
      this.sortedScenarios = this.sortedScenario()
    },
    sortIcon(s) {
      if (s === this.currentSort) {
        if (this.currentSortDir === 'asc') {
          return this.ascIcon
        } else {
          return this.descIcon
        }
      } else {
        return ''
      }
    },
    closeModal () {
      this.scenarioToDelete = null
      this.showModal = false
    },
    openModal (scenario) {
      this.scenarioToDelete = scenario
      this.title = this.$i18n.messages[this.$i18n.locale].scenarios.removeScenario + '?'
      this.showModal = true
    },
    confirmAction () {
      if (this.scenarioToDelete) {
        this.removeScenario()
        this.scenarioToDelete = null
      }
      this.closeModal()
    },
    removeScenario () {
      this.$http.delete(`${api}/${this.scenarioToDelete.id}`, {headers: this.prepareSecurityHeader()})
        .then((response) => {
          this.openNotificationWithType('info', this.$i18n.messages[this.$i18n.locale].notification.scenario.scenarioHasBeenRemoved)
          this.loadData()
        })
        .catch((error) => {
          console.log(error)
          this.openNotificationWithType('danger', this.$i18n.messages[this.$i18n.locale].notification.scenario.errorRemoveScenario)
        })
    },
    isRemove (index) {
      return (this.isDependentCampaign(index))
    },
    isDependentCampaign (index) {
      return !this.scenarios[index].inUse
    },
    prepareSecurityHeader() {
      console.log(this.authenticationToken)
      return {
        Authorization: `Bearer ${this.authenticationToken}`
      }
    },
    onClickFirstPage() {
      this.onPageChange(1)
    },
    onClickPreviousPage() {
      this.onPageChange(this.currentPage - 1)
    },
    onClickPage(page) {
      this.onPageChange(page)
    },
    onClickNextPage() {
      this.onPageChange(this.currentPage + 1)
    },
    onClickLastPage() {
      this.onPageChange(this.totalPages)
    },
    isPageActive(page) {
      return this.currentPage === page
    },
    onPageChange(page) {
      this.currentPage = page
      this.sortedScenarios = this.sortedScenario()
    },
    loadState () {
      this.$http({
        url: PROFILES_API + '/activeState/' + this.loggedId,
        headers: this.prepareSecurityHeader()
      }).then((response) => {
        this.isActive = response.data
        if (!this.isActive) {
          openMessage({
            message: 'Ваша учетная запись не активна. Создание, редактирование и сохранение сценариев и рассылок невозможно',
            type: 'danger',
            showCloseButton: true
          })
        }
      }).catch((error) => {
        console.log(error)
        this.openNotificationWithType('danger', this.$i18n.messages[this.$i18n.locale].notification.scenario.errorToGetUserStatus)
      })
    },
    getDate(date) {
      var d = new Date(Number(date))
      var day = d.getUTCDate()
      var month = d.getUTCMonth() + 1
      var year = d.getUTCFullYear()
      if (day < 10) day = '0' + day
      if (month < 10) month = '0' + month
      return day + '.' + month + '.' + year
    },
    openNotificationWithType (type, message) {
      openNotification({
        title: this.$i18n.messages[this.$i18n.locale].notifications.notification,
        message: message,
        type: type
      })
    }
  }
}
</script>

<style lang="scss">
.table-responsive {
  display: block;
  width: 100%;
  min-height: .01%;
  overflow-x: auto;
}
.button {
  margin: 0 0 8px 0;
}

.pagination {
  list-style-type: none;
}

.pagination-item {
  display: inline-block;
}

.activepage {
  background-color: #4AAE9B;
  color: #ffffff;
}

.table > thead > tr > th {
  text-align: center;
}

</style>



// WEBPACK FOOTER //
// Scenarios.vue?5911bed6